import { LC_SCREENSHOT } from '@mm/api-routes';
import { Heading } from '@mm/components';
import { getDeviceInfo } from '@mm/hooks';
import { CloseCircleIcon } from '@mm/icons';
import { useDomainCRUDActions } from '@mm/state/actions/DomainCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { domainSelector } from '@mm/state/selectors/domainSelector';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ChatWidgetPreviewChannelOption,
  ChatWidgetPreviewChannelOptionLabels,
  ChatWidgetPreviewChannelOptions,
  ChatWidgetPreviewFrame,
} from '../../../editor/features/startBotPromoters/customerChat/CustomerChatEditor/ChatWidgetPreviewFrame/ChatWidgetPreviewFrame';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import Input from '../../components/Input/Input';
import RadioInput from '../../components/RadioInput';
import WidgetSpinner from '../../components/WidgetSpinner/widgetSpinner';
import './ChatWidgetPreviewModal.pcss';

type Props = {
  closeModal: () => void;
  chatWidget: PromoterModel;
};

export const ChatWidgetPreviewModal = (props: Props) => {
  const { closeModal, chatWidget } = props;

  const { isMobile } = getDeviceInfo();
  const { update: updateDomains } = useDomainCRUDActions();

  const domain = useSelector(domainSelector);
  const fbPage = useSelector(activeFacebookPageSelector);
  const userAuthToken = useSelector(
    (state: StoreState) => state.session.session.authToken,
  );
  const chatbot = useSelector(
    (state: StoreState) => state.chatbot.activeChatbot,
  );
  const chatWidgetChannel = chatbot?.chatWidgetChannel;

  const [userProvidedUrl, setUserProvidedUrl] = useState('');
  const [addressBarUrl, setAddressBarUrl] = useState('');
  const [channel, setChannel] = useState<ChatWidgetPreviewChannelOption>(
    chatWidgetChannel === 'facebook' ? 'facebook' : 'web_chat',
  );

  const [siteImage, setSiteImage] = useState<string>();
  const [siteImageError, setSiteImageError] = useState(false);

  const whitelistedUrl =
    domain.domainsList &&
    domain.domainsList.filter(
      d =>
        d.indexOf(window.location.hostname) === -1 &&
        d.indexOf('mobilemonkey.com') === -1,
    )[0];
  const siteUrl = userProvidedUrl || whitelistedUrl;

  useEffect(() => {
    const fetchSiteImage = async () => {
      const siteImageUrl =
        siteUrl &&
        LC_SCREENSHOT(siteUrl, window.innerWidth, window.innerHeight - 71);
      if (!siteImageUrl || !fbPage) {
        return;
      }
      setSiteImageError(false);
      setSiteImage('');
      const response = await fetch(siteImageUrl, {
        method: 'GET',
        headers: {
          'MM-User-Authtoken': userAuthToken,
          'MM-Remote-Page-Id': fbPage.remoteId,
        },
      });
      if (response.status === 200) {
        setSiteImage(URL.createObjectURL(await response.blob()));
      } else {
        setSiteImageError(true);
      }
      if (siteUrl) {
        try {
          const siteUrlOrigin = new URL(siteUrl).origin;
          if (
            !domain.domainsList?.find(d => d === siteUrlOrigin) &&
            !domain.unavailable
          ) {
            updateDomains({
              id: domain.id,
              connectedPageId: domain.connectedPageId,
              domainsList: [
                ...(domain.domainsList || []),
                new URL(siteUrl).origin,
              ],
            });
          }
        } catch {}
      }
    };
    if (siteUrl) {
      setAddressBarUrl(url => url || siteUrl);
      fetchSiteImage();
    }
  }, [fbPage, userAuthToken, siteUrl]);

  const applyAddressBarUrl = (e: React.FormEvent) => {
    e.preventDefault();
    const newUrl =
      addressBarUrl.search('://') === -1
        ? `https://${addressBarUrl}`
        : addressBarUrl;
    setUserProvidedUrl(newUrl);
    setAddressBarUrl(newUrl);
  };

  return (
    <div className="chat-widget-preview-modal">
      <div className="chat-widget-preview-modal__controls">
        <div className="chat-widget-preview-modal__heading">
          <Heading size={3}>Test your chat widget below</Heading>
          {isMobile && (
            <CloseCircleIcon
              className="chat-widget-preview-modal__close-button chat-widget-preview-modal__close-button-icon"
              onClick={closeModal}
            />
          )}
        </div>
        <form
          className="chat-widget-preview-modal__address-bar"
          onSubmit={applyAddressBarUrl}
        >
          <Input
            className="chat-widget-preview-modal__address-bar-input"
            placeholder="Enter your website URL"
            value={addressBarUrl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAddressBarUrl(e.target.value)
            }
          />
          <PrimaryButton
            className="chat-widget-preview-modal__generate-preview-button"
            type="submit"
          >
            Generate preview
          </PrimaryButton>
        </form>
        <div className="chat-widget-preview-modal__channels">
          {ChatWidgetPreviewChannelOptions.map(c => (
            <RadioInput
              className="chat-widget-preview-modal__channel"
              key={c}
              checked={channel === c}
              name="channels"
              label={ChatWidgetPreviewChannelOptionLabels[c]}
              value={c}
              onChange={() => setChannel(c)}
            />
          ))}
        </div>
        <div style={{ flexGrow: 1 }}>&nbsp;</div>
        {!isMobile && (
          <PrimaryButton
            color={ButtonColor.secondaryBlue}
            className="chat-widget-preview-modal__close-button"
            onClick={closeModal}
          >
            Close Preview
          </PrimaryButton>
        )}
      </div>
      <div className="chat-widget-preview-modal__preview-area">
        {siteImageError && (
          <div className="chat-widget-preview-modal__preview-error">
            Failed to load site preview
          </div>
        )}
        {!siteImageError && !siteImage && siteUrl && (
          <div className="chat-widget-preview-modal__preview-loading-indicator">
            <WidgetSpinner />
            Loading site preview
          </div>
        )}
        {!siteImageError && siteImage && siteUrl && (
          <img
            className="chat-widget-preview-modal__preview-image"
            src={siteImage}
            alt={`Preview of ${siteUrl}`}
          />
        )}
      </div>
      <ChatWidgetPreviewFrame
        className="chat-widget-preview-modal__widget-iframe"
        chatWidget={chatWidget}
        channel={channel}
        showGreeting
        interactive
      />
    </div>
  );
};
